"use client"
import React, { useEffect, useRef } from "react"

export function BackgroundGraph({ nodeCount = 80, linkDistance = 110, speed = 0.15 }) {
  const canvasRef = useRef(null)
  const rafRef = useRef(null)
  const nodesRef = useRef([])
  const isReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext("2d")
    let width = (canvas.width = canvas.clientWidth * devicePixelRatio)
    let height = (canvas.height = canvas.clientHeight * devicePixelRatio)
    ctx.scale(devicePixelRatio, devicePixelRatio)

    const count = isReducedMotion ? Math.max(20, Math.floor(nodeCount * 0.35)) : nodeCount
    nodesRef.current = Array.from({ length: count }).map(() => ({
      x: Math.random() * (width / devicePixelRatio),
      y: Math.random() * (height / devicePixelRatio),
      vx: (Math.random() - 0.5) * speed,
      vy: (Math.random() - 0.5) * speed,
    }))

    const onResize = () => {
      width = canvas.width = canvas.clientWidth * devicePixelRatio
      height = canvas.height = canvas.clientHeight * devicePixelRatio
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.scale(devicePixelRatio, devicePixelRatio)
    }

    const onVisibility = () => {
      if (document.hidden) {
        if (rafRef.current) cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      } else {
        tick()
      }
    }

    window.addEventListener("resize", onResize)
    document.addEventListener("visibilitychange", onVisibility)

    const bgGradient = ctx.createLinearGradient(0, 0, 0, height / devicePixelRatio)
    bgGradient.addColorStop(0, "rgba(30,58,138,0.05)")
    bgGradient.addColorStop(1, "rgba(16,185,129,0.05)")

    const tick = () => {
      ctx.clearRect(0, 0, width / devicePixelRatio, height / devicePixelRatio)
      ctx.fillStyle = bgGradient
      ctx.fillRect(0, 0, width / devicePixelRatio, height / devicePixelRatio)

      const nodes = nodesRef.current

      for (const n of nodes) {
        n.x += n.vx
        n.y += n.vy
        if (n.x < 0 || n.x > width / devicePixelRatio) n.vx *= -1
        if (n.y < 0 || n.y > height / devicePixelRatio) n.vy *= -1
      }

      ctx.lineWidth = 1
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i]
          const b = nodes[j]
          const dx = a.x - b.x
          const dy = a.y - b.y
          const dist = Math.hypot(dx, dy)
          if (dist < linkDistance) {
            const alpha = 1 - dist / linkDistance
            ctx.strokeStyle = `rgba(37, 99, 235, ${0.12 * alpha})`
            ctx.beginPath()
            ctx.moveTo(a.x, a.y)
            ctx.lineTo(b.x, b.y)
            ctx.stroke()
          }
        }
      }

      for (const n of nodes) {
        ctx.beginPath()
        ctx.arc(n.x, n.y, 2.2, 0, Math.PI * 2)
        ctx.fillStyle = "rgba(37,99,235,0.20)"
        ctx.fill()
      }

      if (!isReducedMotion) {
        rafRef.current = requestAnimationFrame(tick)
      }
    }

    tick()

    return () => {
      window.removeEventListener("resize", onResize)
      document.removeEventListener("visibilitychange", onVisibility)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [nodeCount, linkDistance, speed, isReducedMotion])

  return (
    <div className="pointer-events-none absolute inset-0 -z-10">
      <canvas ref={canvasRef} className="h-full w-full" aria-hidden />
      <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-black/5" />
    </div>
  )
}
